import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { DeleteAction } from '~/features/document-bulk-actions/DeleteAction';
import { api } from '~/redux/api/createApi';
import { PilotId } from '~/types/PilotId';

interface Props {
  isVisible: boolean;
  documentIds: PilotId[];
  onHide: () => void;
  onSuccess?: () => void;
}

type DeleteDataType = Pick<
  React.ComponentProps<typeof DeleteAction>,
  'reason' | 'comment'
>;

export const DeleteActionModal = ({
  documentIds,
  isVisible,
  onHide,
  onSuccess,
}: Props) => {
  const { toast } = useToast();
  const intl = useIntl();
  const [{ reason, comment }, setDeleteData] = useState<DeleteDataType>({
    reason: null,
    comment: '',
  });
  const hasDuplicates = documentIds.length > 1;
  const [deleteDocument, { isLoading }] =
    api.endpoints[
      hasDuplicates ? 'searchV2BulkDelete' : 'deleteDocument'
    ].useMutation();

  const handleDelete = async () => {
    try {
      await deleteDocument({
        reason: reason ?? '',
        extraDetails: comment,
        documentId: documentIds[0],
        documentHandlerIds: documentIds,
        isAllSelected: false,
        searchAPI: 'v3',
      }).unwrap();
      toast({
        status: 'success',
        message: intl.formatMessage(
          {
            id: 'DeleteActionModal.successMessage',
            defaultMessage:
              '{count, plural, one {Document} other {Documents}} deleted successfully.',
            description:
              'Success message when documents are deleted. Example: count = 3',
          },
          { count: documentIds.length },
        ),
      });
      onSuccess?.();
      onHide();
    } catch {
      toast({
        status: 'danger',
        message: intl.formatMessage(
          {
            id: 'DeleteActionModal.errorMessage',
            defaultMessage:
              'An error occurred while deleting the {count, plural, one {Document} other {Documents}}.',
            description:
              'Error message when document deletion fails. Example: count = 3',
          },
          { count: documentIds.length },
        ),
      });
    }
  };

  return (
    <Modal
      title={intl.formatMessage(
        {
          id: 'DeleteActionModal.title',
          defaultMessage:
            'Delete {count, plural, one {Document} other {Documents}}',
          description: 'Modal title for deleting documents. Example: count = 3',
        },
        { count: documentIds.length },
      )}
      isVisible={isVisible}
      onHide={onHide}
      primaryAction={{
        text: intl.formatMessage({
          id: 'DeleteActionModal.deleteButton',
          defaultMessage: 'Delete',
          description: 'Button text to confirm delete action',
        }),
        level: 'danger',
        icon: 'trash',
        onClick: handleDelete,
        isLoading,
      }}
    >
      <DeleteAction
        shouldShowDuplicateWarning={documentIds.length > 1}
        reason={reason}
        comment={comment}
        onChange={setDeleteData}
        selectedCount={1}
        type="document"
      />
    </Modal>
  );
};
